/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TabNav from "../../components/TabNav";

import DataTable, { Column } from "components/DataTable";
import { StatusChip } from "components/StatusChip";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useHasRole } from "contexts/AuthContext";
import { fetchAdminUnitDetail } from "services/advocateComplaintService";
import {
  fetchComplaint,
  fetchComplaintForProsecutor,
  searchComplaint,
} from "services/prosecutorDecipilineService";
import { AdminUnit, Complaints } from "types";
import { COMPLIANT_STATUS } from "utils/constantValues";
import { DateView } from "utils/helper";
import { ROLES } from "utils/roles";
import "./complaintRequest.css";

const tabs = [
  "All",
  COMPLIANT_STATUS.PENDING,
  COMPLIANT_STATUS.UNDER_INVESTIGATION,
  COMPLIANT_STATUS.DRAFT_CHARGE,
  COMPLIANT_STATUS.UNDER_LITIGATION,
  COMPLIANT_STATUS.CLOSED,
  // Special Search tab will be conditionally added below
];

const ComplaintRequest: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreateComplaint = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  // const canViewAllComplaint = useHasRole([
  //   ROLES.INSPECTION_COORDINATOR,
  //   ROLES.INSPECTION_DIRECTOR,
  //   ROLES.INSPECTION_REGISTRAR,
  // ]);
  // const canOnlyViewAssignedComplaint = useHasRole([
  //   ROLES.INSPECTION_PROSECUTOR,
  // ]);
  // const canOnlyViewCouncilComplaint = useHasRole([
  //   ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  //   ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  //   ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  //   ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  // ]);

  const specialSearch = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const isCouncelWritter = useHasRole([ROLES.REGIONAL_COUNCIL_OFFICE_WRITER]);
  const isProsecutor = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  const byAdminUnit = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);

  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();

  const [activeTab, setActiveTab] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  // const [loading, setLoading] = useState(true);
  // const [error, setError] = useState("");
  const [complaints, setComplaints] = useState<Complaints[]>([]);
  const [zonalComplaints, setZonalComplaints] = useState<Complaints[]>([]);
  const [specialSearchResults, setSpecialSearchResults] = useState<
    Complaints[]
  >([]);
  const [isSpecialSearchLoading, setIsSpecialSearchLoading] = useState(false);
  // Debounce timer ref for special search
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Server-side search for specialSearch tab with debounce
  const handleSpecialSearch = (query: string) => {
    if (!specialSearch) return;
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(async () => {
      setIsSpecialSearchLoading(true);
      try {
        const response = await searchComplaint(query); // Replace with actual API if different
        setSpecialSearchResults(response || []);
      } catch (err) {
        setSpecialSearchResults([]);
      } finally {
        setIsSpecialSearchLoading(false);
      }
    }, 400); // 400ms debounce
  };

  useEffect(() => {
    const loadInitialPageData = async () => {
      setIsInitialLoading(true);

      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        ).catch(() => undefined);
        setAdminUnitData(adminUnitDetail);

        const api = isProsecutor
          ? fetchComplaintForProsecutor(user.user_id)
          : fetchComplaint();

        const data = await api;
        const filteredData = byAdminUnit
          ? data?.filter((c) => c.admin_unit_id === user.admin_unit)
          : data;

        setComplaints(filteredData || []);
        setZonalComplaints(
          (data || [])?.filter((c) => c.is_regional === false),
        );
      } catch (err) {
        setComplaints([]);
        setZonalComplaints([]);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialPageData();
  }, [
    user?.admin_unit,
    user?.access,
    user?.user_id,
    isProsecutor,
    byAdminUnit,
  ]);

  function newComplaint() {
    navigate("/new-complaint");
  }

  const columns: Column<Complaints>[] = [
    {
      header: "Application Number",
      accessor: "accused_prosecutor_working_id",
      render: (row) => {
        if (typeof row.application_number === "string") {
          return row.application_number || "—";
        }
        return row.application_number || "—";
      },
    },
    {
      header: t("accusedProsecutor"),
      accessor: "accused_prosecutor_name",
      render: (row) => (
        <div className="flex items-center gap-3">
          <img
            src={require("../../images/avatar.png")}
            className="w-10 h-10 rounded-full grayscale"
          />
          <span>{row.accused_prosecutor_name}</span>
        </div>
      ),
      widthClass: "w-1/5",
    },
    {
      header: t("assignedTo"),
      accessor: "assignedTo",
      render: (row) => row.assigned_prosecutor ?? "—",
    },
    {
      header: t("appliedDate"),
      accessor: "reception_date",
      render: (row) => DateView(row.reception_date),
    },
    {
      header: t("status"),
      accessor: "status",
      render: (row) => <StatusChip status={row?.status || ""} />,
    },
    {
      header: t("phoneNumber"),
      accessor: "accused_prosecutor_phone",
    },
    {
      header: t("assign"),
      accessor: "id",
      render: (row) =>
        row.assigned_prosecutor ? (
          <span className="text-green-600 font-medium">{t("assigned")}</span>
        ) : (
          <button disabled className="px-4 py-1 text-sm bg-gray-200 rounded ">
            {t("assign")}
          </button>
        ),
    },
    {
      header: t("action"),
      accessor: "action",
      render: (row) => (
        <button
          onClick={() => navigate(`/complaint-request/${row?.uu_id}`)}
          className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800 transition"
        >
          {t("open")}
        </button>
      ),
    },
  ];

  // Tabs: add "Zonal Complaints" and "Special Search" if needed
  const tabList: string[] = [
    ...tabs,
    !adminUnitData?.parent_unit && byAdminUnit ? "Zonal Complaints" : null,
    specialSearch ? "Special Search" : null,
  ].filter((tab): tab is string => Boolean(tab));

  return (
    <div className=" mx-auto flex flex-col space-y-4">
      <h1 className="text-2xl font-semibold text-gray-700 flex items-center gap-2 mb-5">
        <span>⚖</span>
        {t("pageTitle")}
      </h1>

      {/* Header: Tabs + Add button */}
      <div className="w-full flex flex-wrap items-center justify-between gap-4 mb-5">
        <TabNav active={activeTab} tabs={tabList} onSelect={setActiveTab} />

        {canCreateComplaint && (
          <button
            onClick={newComplaint}
            className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded hover:bg-green-700 transition"
          >
            {t("addNewComplaint")}
          </button>
        )}
      </div>

      {/* DataTable for each tab */}
      {activeTab === "Special Search" ? (
        <DataTable
          title="← Special Search Results"
          columns={columns}
          isLoading={isSpecialSearchLoading}
          loadingText="Searching..."
          data={specialSearchResults}
          rowKey={(row) => row.uu_id}
          onSearch={(q) => {
            setSearchQuery(q);
            handleSpecialSearch(q);
          }}
        />
      ) : (
        <DataTable
          title="← Service Request Table"
          columns={columns}
          isLoading={isInitialLoading}
          loadingText="Loading"
          data={
            activeTab === "Zonal Complaints"
              ? (zonalComplaints || [])
                  .sort(
                    (a, b) =>
                      new Date(b?.createdAt ?? "").getTime() -
                      new Date(a?.createdAt ?? "").getTime(),
                  )
                  ?.filter((obj) => {
                    if (!searchQuery.trim()) return true;

                    const query = searchQuery.toLowerCase();
                    const accusedName =
                      obj.accused_prosecutor_name?.toLowerCase() || "";
                    const licenseNumber =
                      typeof obj.accused_prosecutor === "string"
                        ? obj.accused_prosecutor.toLowerCase()
                        : obj.accused_prosecutor?.working_id?.toLowerCase() ||
                          "";

                    return (
                      accusedName.includes(query) ||
                      licenseNumber.includes(query)
                    );
                  })
              : (complaints || [])
                  .sort(
                    (a, b) =>
                      new Date(b?.createdAt ?? "").getTime() -
                      new Date(a?.createdAt ?? "").getTime(),
                  )
                  ?.filter(
                    (obj) => activeTab === "All" || obj.status === activeTab,
                  )
                  ?.filter((obj) =>
                    isCouncelWritter
                      ? obj?.admin_unit_id === user?.admin_unit ||
                        obj?.is_final_decison_given
                      : true,
                  )
                  ?.filter((obj) => {
                    if (!searchQuery.trim()) return true;

                    const query = searchQuery.toLowerCase();
                    const accusedName =
                      obj.accused_prosecutor_name?.toLowerCase() || "";
                    const licenseNumber =
                      typeof obj.accused_prosecutor === "string"
                        ? obj.accused_prosecutor.toLowerCase()
                        : obj.accused_prosecutor?.working_id?.toLowerCase() ||
                          "";

                    return (
                      accusedName.includes(query) ||
                      licenseNumber.includes(query)
                    );
                  })
          }
          rowKey={(row) => row.uu_id}
          onSearch={(q) => setSearchQuery(q)}
        />
      )}
    </div>
  );
};

export default ComplaintRequest;
